import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import useCrmEvent from 'hooks/useCrmEvent';
import { M } from 'TOMA';
import routes from 'router/routes';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { iProspectFile, eCrmProspectStatus } from 'services/Crm/Crm.type';
import KanbanColumn from './components/KanbanColumn';
import KanbanCard from './components/KanbanCard';
import s from './CrmPipeline.module.scss';

const KANBAN_STATUSES: eCrmProspectStatus[] = [
  'new',
  'contacted',
  'qualified',
  'proposal',
  'negotiation',
  'won',
];

function CrmPipeline(): JSX.Element {
  const navigate = useNavigate();
  const { crmRepository } = useApiContext();
  const { selectedEventId, setSelectedEventId, events, eventsLoading } =
    useCrmEvent();
  const { t } = useTranslation();
  const [reload, setReload] = useState(0);
  const [activeId, setActiveId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const {
    response: prospects = [],
    loading,
    error,
  } = useRepo(async () => {
    if (selectedEventId) {
      return await crmRepository.getProspectsByEvent(selectedEventId);
    }
    return [] as iProspectFile[];
  }, [selectedEventId, reload]);

  const grouped = useMemo(() => {
    const map: Record<string, iProspectFile[]> = {};
    for (const status of KANBAN_STATUSES) {
      map[status] = [];
    }
    for (const p of prospects ?? []) {
      if (map[p.status] != null) {
        map[p.status].push(p);
      }
    }
    return map;
  }, [prospects]);

  const activeProspect = useMemo(
    () => prospects?.find((p: iProspectFile) => p.id === activeId) ?? null,
    [prospects, activeId]
  );

  const totalValue = useMemo(() => {
    return (prospects ?? [])
      .filter(
        (p: iProspectFile) => p.status !== 'lost' && p.estimated_budget != null
      )
      .reduce(
        (sum: number, p: iProspectFile) => sum + (p.estimated_budget ?? 0),
        0
      );
  }, [prospects]);

  const activeCount = useMemo(() => {
    return (prospects ?? []).filter(
      (p: iProspectFile) => !['won', 'lost'].includes(p.status)
    ).length;
  }, [prospects]);

  const avgDeal = activeCount > 0 ? Math.round(totalValue / activeCount) : 0;

  const handleDragStart = (event: DragStartEvent): void => {
    setActiveId(Number(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const prospectId = Number(active.id);
    const newStatus = String(over.id) as eCrmProspectStatus;

    const prospect = prospects?.find((p: iProspectFile) => p.id === prospectId);
    if (!prospect || prospect.status === newStatus) return;

    try {
      await crmRepository.updateProspectStatus(prospectId, newStatus);
      toast.success(t('crm:prospect_moved'));
      setReload((r) => r + 1);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>{t('crm:pipeline_title')}</h2>
          <div className={s.pipelineStats}>
            <div className={s.pipeStat}>
              <span className={s.pipeStatLabel}>
                {t('crm:pipeline_total_value')}
              </span>
              <span className={s.pipeStatValue}>
                {totalValue.toLocaleString()} &euro;
              </span>
            </div>
            <div className={s.pipeStat}>
              <span className={s.pipeStatLabel}>
                {t('crm:pipeline_avg_deal')}
              </span>
              <span className={s.pipeStatValue}>
                {avgDeal.toLocaleString()} &euro;
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select
            value={selectedEventId ?? ''}
            onChange={(e) => setSelectedEventId(Number(e.target.value))}
            className={s.eventSelect}
          >
            {(events ?? []).map((event: any) => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
          <button
            className={s.addBtn}
            onClick={() =>
              navigate(
                `${routes.crmProspectNew.path}${
                  selectedEventId ? `?eventId=${selectedEventId}` : ''
                }`
              )
            }
          >
            {t('crm:new_prospect_btn')}
          </button>
        </div>
      </div>

      <M.Loading loading={loading || eventsLoading} error={error} fullWiewport>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className={s.board}>
            {KANBAN_STATUSES.map((status) => (
              <KanbanColumn
                key={status}
                status={status}
                prospects={grouped[status] ?? []}
              />
            ))}
          </div>
          <DragOverlay>
            {activeProspect != null ? (
              <KanbanCard prospect={activeProspect} isOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>
      </M.Loading>
    </div>
  );
}

export default CrmPipeline;
